import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage } from '../i18n/LanguageContext';
import { 
  CheckCircle, 
  Loader2,
  Calendar,
  Users,
  Mail,
  Phone,
  ChevronRight,
  AlertCircle
} from 'lucide-react';

const BookingSuccessPage = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId || !bookingId) {
        setError(t('success.missingInfo'));
        setLoading(false);
        return;
      }

      try {
        // Poll payment status
        let attempts = 0;
        const maxAttempts = 5;
        const pollInterval = 2000;

        const poll = async () => {
          if (attempts >= maxAttempts) {
            setError(t('success.timedOut'));
            setLoading(false);
            return;
          }

          try {
            const status = await api.getPaymentStatus(sessionId);
            setPaymentStatus(status);

            if (status.payment_status === 'paid') {
              const bookingData = await api.getBooking(bookingId);
              setBooking(bookingData);
              setLoading(false);
              
              // Google Ads tracking - Purchase event
              if (window.gtag && bookingData) {
                window.gtag('event', 'purchase', {
                  'value': bookingData.total_price,
                  'currency': 'USD',
                  'transaction_id': bookingData.id
                });
              }
            } else if (status.status === 'expired') {
              setError(t('success.sessionExpired'));
              setLoading(false);
            } else {
              attempts++;
              setTimeout(poll, pollInterval);
            }
          } catch (err) {
            console.error('Error polling status:', err);
            attempts++;
            setTimeout(poll, pollInterval);
          }
        };

        poll();
      } catch (err) {
        console.error('Error:', err);
        setError(t('success.failedVerify'));
        setLoading(false);
      }
    };

    checkPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, bookingId]);

  const locale = language === 'es' ? 'es-ES' : 'en-US';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Navbar />
        <div className="pt-32 pb-16 flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 text-[#1D4E41] animate-spin mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">{t('success.verifying')}</h2>
          <p className="text-gray-500 mt-2">{t('success.pleaseWait')}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Navbar />
        <div className="pt-32 pb-16 flex flex-col items-center justify-center min-h-[60vh]">
          <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700">{t('success.paymentIssue')}</h2>
          <p className="text-gray-500 mt-2 text-center max-w-md">{error}</p>
          <div className="flex gap-4 mt-8">
            <Link
              to="/tours"
              data-testid="back-to-tours"
              className="h-12 px-6 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-medium flex items-center gap-2 transition-all"
            >
              {t('success.browseTours')}
            </Link>
            <a
              href="https://wa.me/50688173167?text=Hello!%20I%20need%20help%20with%20my%20booking%20payment."
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 px-6 border-2 border-[#1D4E41] text-[#1D4E41] hover:bg-[#1D4E41] hover:text-white rounded-full font-medium flex items-center gap-2 transition-all"
            >
              {t('success.contactSupport')}
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <WhatsAppButton />

      <section className="pt-32 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          {/* Success Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#1D4E41]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-3" data-testid="success-title">
              {t('success.title')}
            </h1>
            <p className="text-gray-600 text-lg">
              {t('success.subtitle')}
            </p>
          </div>

          {/* Booking Details Card */}
          {booking && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="booking-details">
              <div className="bg-[#1D4E41] text-white p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-white/70">{t('success.bookingReference')}</div>
                    <div className="text-xl font-bold">{booking.id.slice(0, 8).toUpperCase()}</div>
                  </div>
                  <div className="px-4 py-2 bg-white/20 rounded-full text-sm font-medium">
                    {t('success.confirmed')}
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Tour Info */}
                <div>
                  <h3 className="font-semibold text-lg text-[#1A1A1A] mb-1">{booking.tour_title}</h3>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {/* Fix timezone issue: parse date parts directly */}
                      {(() => {
                        const [year, month, day] = booking.date.split('-').map(Number);
                        const travelDate = new Date(year, month - 1, day);
                        return travelDate.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                      })()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {booking.passengers} {booking.passengers === 1 ? t('success.passenger') : t('success.passengersPlural')}
                    </span>
                  </div>
                </div>

                {/* Booking Date Info */}
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                  <span className="font-medium">{t('success.bookedOn')}</span> {new Date(booking.created_at || Date.now()).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>

                {/* Contact Info */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="text-sm text-gray-500 mb-2">{t('success.contactInformation')}</div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#1A1A1A]">{booking.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4" />
                      {booking.customer_email}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4" />
                      {booking.customer_phone}
                    </div>
                  </div>
                </div>

                {/* Payment Info */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t('success.totalPaid')}</span>
                    <span className="text-2xl font-bold text-[#1D4E41]">${booking.total_price}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="mt-8 bg-[#E8F5E9] rounded-2xl p-6">
            <h3 className="font-semibold text-[#1D4E41] mb-4">{t('success.whatNext')}</h3>
            <ul className="space-y-3 text-sm text-[#1D4E41]">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{t('success.step1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{t('success.step2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{t('success.step3')}</span>
              </li>
            </ul>
          </div>

          {/* Email Notice */}
          <div className="mt-4 bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <Mail className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800 mb-2">📬 {t('success.checkEmail')}</h4>
                <p className="text-sm text-amber-700">
                  {t('success.emailFrom')} <strong>reservations@travelguide.cr</strong>
                </p>
                <p className="text-sm text-amber-600 mt-2">
                  {t('success.checkSpam')} <strong>{t('success.spamFolder')}</strong> {t('success.or')} <strong>{t('success.junkFolder')}</strong> {t('success.markNotSpam')}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/tours"
              data-testid="browse-more-tours"
              className="h-12 px-8 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-medium flex items-center justify-center gap-2 transition-all"
            >
              {t('success.browseMore')}
              <ChevronRight className="w-5 h-5" />
            </Link>
            <a
              href="https://wa.me/50688173167?text=Hello!%20I%20just%20made%20a%20booking%20and%20have%20a%20question."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="h-12 px-8 border-2 border-[#1D4E41] text-[#1D4E41] hover:bg-[#1D4E41] hover:text-white rounded-full font-medium flex items-center justify-center gap-2 transition-all"
            >
              {t('success.contactWhatsapp')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookingSuccessPage;
